'use client';

import React, { useState } from 'react';
import { ExternalLink, ChevronDown, ChevronUp, MapPin, Clock, TrendingUp, Mountain, Zap, CheckCircle2, AlertCircle } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


interface KomSuggestion {
  id: string;
  activityName: string;
  activityDate: string;
  activityType: 'Ride' | 'Run';
  segmentName: string;
  startLat: number;
  startLng: number;
  endLat: number;
  endLng: number;
  distance: number;
  elevationGain: number;
  avgGrade: number;
  yourTime: string;
  yourSpeed: number;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
  stravaActivityId: string;
}

const MOCK_SUGGESTIONS: KomSuggestion[] = [
  {
    id: 'sug-001',
    activityName: 'Morning Climb — Flagstaff Rd',
    activityDate: '2024-04-22',
    activityType: 'Ride',
    segmentName: 'Flagstaff Upper Hairpins',
    startLat: 39.9988,
    startLng: -105.3012,
    endLat: 40.0021,
    endLng: -105.3098,
    distance: 2.1,
    elevationGain: 198,
    avgGrade: 9.4,
    yourTime: '6:12',
    yourSpeed: 20.3,
    confidence: 'high',
    reason: 'No segment covers this 2.1 km stretch. You held the fastest pace of any rider tracked through this GPS corridor in the last 90 days.',
    stravaActivityId: '11234567890',
  },
  {
    id: 'sug-002',
    activityName: 'Tuesday Intervals — Boulder Creek',
    activityDate: '2024-04-19',
    activityType: 'Ride',
    segmentName: 'Boulder Creek Eastbound Blast',
    startLat: 40.0142,
    startLng: -105.2812,
    endLat: 40.0138,
    endLng: -105.2601,
    distance: 1.7,
    elevationGain: 4,
    avgGrade: 0.2,
    yourTime: '3:08',
    yourSpeed: 32.5,
    confidence: 'high',
    reason: 'Flat corridor with no existing segment. Your effort here is 8% faster than the next-best recorded time in this GPS corridor.',
    stravaActivityId: '11198765432',
  },
  {
    id: 'sug-003',
    activityName: 'Long Ride — Lefthand Canyon',
    activityDate: '2024-04-14',
    activityType: 'Ride',
    segmentName: 'Lefthand Canyon Mid-Section',
    startLat: 40.1023,
    startLng: -105.3345,
    endLat: 40.1187,
    endLng: -105.3512,
    distance: 3.4,
    elevationGain: 267,
    avgGrade: 7.8,
    yourTime: '11:44',
    yourSpeed: 17.4,
    confidence: 'medium',
    reason: 'Existing segments cover the lower and upper sections, but this 3.4 km middle stretch is uncharted. You were the fastest through it this month.',
    stravaActivityId: '11087654321',
  },
  {
    id: 'sug-004',
    activityName: 'Gravel Grinder — Marshall Mesa',
    activityDate: '2024-04-10',
    activityType: 'Ride',
    segmentName: 'Marshall Mesa South Descent',
    startLat: 39.9712,
    startLng: -105.2234,
    endLat: 39.9654,
    endLng: -105.2189,
    distance: 0.9,
    elevationGain: -82,
    avgGrade: -9.1,
    yourTime: '1:47',
    yourSpeed: 30.2,
    confidence: 'high',
    reason: 'Short, punchy descent with no segment. Your speed here is consistently the fastest of any rider who has passed through this corridor.',
    stravaActivityId: '10976543210',
  },
  {
    id: 'sug-005',
    activityName: 'Easy Spin — Diagonal Hwy',
    activityDate: '2024-04-07',
    activityType: 'Ride',
    segmentName: 'Diagonal Hwy Northbound Stretch',
    startLat: 40.0521,
    startLng: -105.1987,
    endLat: 40.0712,
    endLng: -105.1965,
    distance: 2.3,
    elevationGain: 18,
    avgGrade: 0.8,
    yourTime: '4:22',
    yourSpeed: 31.6,
    confidence: 'medium',
    reason: 'Busy road corridor with no segment. You recorded the fastest time through this stretch compared to other Strava athletes in the past 60 days.',
    stravaActivityId: '10865432109',
  },
  {
    id: 'sug-006',
    activityName: 'Tempo Run — Wonderland Lake',
    activityDate: '2024-04-03',
    activityType: 'Run',
    segmentName: 'Wonderland Lake North Trail',
    startLat: 40.0389,
    startLng: -105.2901,
    endLat: 40.0421,
    endLng: -105.2834,
    distance: 0.7,
    elevationGain: 22,
    avgGrade: 3.1,
    yourTime: '2:41',
    yourSpeed: 15.6,
    confidence: 'low',
    reason: 'Trail section with limited data. You appear to be the fastest runner through this GPS corridor, but fewer than 10 athletes have been tracked here.',
    stravaActivityId: '10754321098',
  },
];

const CONFIDENCE_CONFIG = {
  high: { label: 'Strong match', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-100', dot: 'bg-emerald-500' },
  medium: { label: 'Likely match', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-100', dot: 'bg-amber-500' },
  low: { label: 'Possible match', color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-400' },
};

function ConfidenceBadge({ confidence }: { confidence: KomSuggestion['confidence'] }) {
  const cfg = CONFIDENCE_CONFIG[confidence];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.bg} ${cfg.border} border ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function StatPill({ icon: Icon, value, label }: { icon: React.ElementType; value: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]">
      <Icon size={11} className="text-[hsl(var(--muted-foreground))] shrink-0" />
      <span className="text-xs font-bold font-mono tabular-nums">{value}</span>
      <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{label}</span>
    </div>
  );
}

export default function KomSuggestionsList() {
  const [expanded, setExpanded] = useState<string | null>('sug-001');
  const [created, setCreated] = useState<Set<string>>(new Set());
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const visible = MOCK_SUGGESTIONS.filter((s) => !dismissed.has(s.id));

  const handleCreate = (suggestion: KomSuggestion) => {
    const url = `https://www.strava.com/segment_creations/new?activity_id=${suggestion.stravaActivityId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setCreated((prev) => new Set(prev).add(suggestion.id));
    toast.success(`Opened Strava to create "${suggestion.segmentName}"`, {
      description: 'Complete the segment creation in the Strava tab.',
      duration: 4000,
    });
  };

  const handleDismiss = (id: string) => {
    setDismissed((prev) => new Set(prev).add(id));
    toast('Suggestion dismissed', { duration: 2000 });
  };

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin p-6">
      <Toaster position="bottom-right" richColors />

      {/* Summary bar */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-[hsl(var(--border))]">
          <Zap size={13} className="text-[hsl(var(--primary))]" />
          <span className="text-xs font-semibold text-[hsl(var(--foreground))]">{visible.length} suggestions</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-[hsl(var(--border))]">
          <CheckCircle2 size={13} className="text-emerald-500" />
          <span className="text-xs font-semibold text-[hsl(var(--foreground))]">
            {MOCK_SUGGESTIONS.filter((s) => s.confidence === 'high').length} high-confidence
          </span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-[hsl(var(--border))]">
          <AlertCircle size={13} className="text-amber-500" />
          <span className="text-xs font-semibold text-[hsl(var(--foreground))]">
            {created.size} sent to Strava
          </span>
        </div>
      </div>

      {visible.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--muted))] flex items-center justify-center mb-4">
            <CheckCircle2 size={24} className="text-[hsl(var(--muted-foreground))]" />
          </div>
          <p className="text-sm font-semibold text-[hsl(var(--foreground))]">All caught up</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">No remaining suggestions. Sync Strava to check for new ones.</p>
        </div>
      )}

      <div className="space-y-3">
        {visible.map((suggestion) => {
          const isExpanded = expanded === suggestion.id;
          const isCreated = created.has(suggestion.id);

          return (
            <div
              key={suggestion.id}
              className={`bg-white border rounded-2xl overflow-hidden transition-all duration-200 ${
                isCreated ? 'border-emerald-200' : 'border-[hsl(var(--border))]'
              }`}
            >
              {/* Card header — always visible */}
              <button
                className="w-full text-left px-5 py-4 flex items-start gap-4"
                onClick={() => setExpanded(isExpanded ? null : suggestion.id)}
              >
                {/* Activity type pill */}
                <div className={`mt-0.5 shrink-0 px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide ${
                  suggestion.activityType === 'Ride' ?'bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary))]' :'bg-blue-50 text-blue-600'
                }`}>
                  {suggestion.activityType}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-[hsl(var(--foreground))] truncate">
                      {suggestion.segmentName}
                    </span>
                    <ConfidenceBadge confidence={suggestion.confidence} />
                    {isCreated && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 border border-emerald-100 text-emerald-700">
                        <CheckCircle2 size={9} />
                        Sent to Strava
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">
                    From <span className="font-medium text-[hsl(var(--foreground))]">{suggestion.activityName}</span>
                    {' · '}
                    {new Date(suggestion.activityDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>

                {/* Quick stats */}
                <div className="hidden sm:flex items-center gap-2 shrink-0">
                  <StatPill icon={MapPin} value={`${suggestion.distance} km`} label="" />
                  <StatPill icon={Clock} value={suggestion.yourTime} label="your time" />
                </div>

                <div className="shrink-0 text-[hsl(var(--muted-foreground))] mt-1">
                  {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                </div>
              </button>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="px-5 pb-5 border-t border-[hsl(var(--border))]">
                  <div className="pt-4 space-y-4">
                    {/* Stats row */}
                    <div className="flex flex-wrap gap-2">
                      <StatPill icon={MapPin} value={`${suggestion.distance} km`} label="distance" />
                      <StatPill icon={Mountain} value={`${suggestion.elevationGain > 0 ? '+' : ''}${suggestion.elevationGain} m`} label="elevation" />
                      <StatPill icon={TrendingUp} value={`${suggestion.avgGrade}%`} label="avg grade" />
                      <StatPill icon={Clock} value={suggestion.yourTime} label="your time" />
                      <StatPill icon={Zap} value={`${suggestion.yourSpeed} km/h`} label="avg speed" />
                    </div>

                    {/* Reason */}
                    <div className="p-3 rounded-xl bg-[hsl(var(--muted))] border border-[hsl(var(--border))]">
                      <p className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-1">Why this was flagged</p>
                      <p className="text-xs text-[hsl(var(--foreground))] leading-relaxed">{suggestion.reason}</p>
                    </div>

                    {/* GPS coords */}
                    <div className="flex items-center gap-4 text-[10px] text-[hsl(var(--muted-foreground))] font-mono">
                      <span>Start: {suggestion.startLat.toFixed(4)}, {suggestion.startLng.toFixed(4)}</span>
                      <span className="text-[hsl(var(--border))]">→</span>
                      <span>End: {suggestion.endLat.toFixed(4)}, {suggestion.endLng.toFixed(4)}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        onClick={() => handleCreate(suggestion)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all active:scale-95 ${
                          isCreated
                            ? 'bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100' :'bg-[hsl(var(--primary))] text-white hover:bg-[#e84400]'
                        }`}
                      >
                        <ExternalLink size={12} />
                        {isCreated ? 'Open Strava again' : 'Create segment on Strava'}
                      </button>
                      <button
                        onClick={() => handleDismiss(suggestion.id)}
                        className="px-4 py-2 rounded-xl text-xs font-medium text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
